import { itineraryApi } from '@/src/api/rtkQuery/itineraryApi'
import type {
    ApiCouponOffer,
    ApplyCouponPayload,
    ApplyCouponResponse,
    RemoveDiscountPayload,
    RemoveDiscountResponse
} from '@/src/types/couponTray'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import fareFamilyApi from './fareFamilyApi'

export const couponApi = createApi({
    reducerPath: 'couponApi',
    baseQuery: fetchBaseQuery({
        credentials: 'include',
        prepareHeaders: headers => {
            headers.set('Content-Type', 'application/json')
            return headers
        }
    }),
    endpoints: builder => ({
        applyCoupon: builder.mutation<ApplyCouponResponse, { itineraryId: string; payload: ApplyCouponPayload }>({
            query: ({ itineraryId, payload }) => ({
                url: `/itin/itinerary/${itineraryId}/applyCoupon`,
                method: 'POST',
                body: payload
            }),
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const couponData = data?.couponData ? data.couponData : []
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (draft?.itineraryData) {
                                draft.itineraryData.couponDetails = couponData
                            }
                        })
                    )
                } catch (e) {
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (draft?.itineraryData) {
                                draft.itineraryData.couponDetails = []
                            }
                        })
                    )
                    console.error('Failed to update coupon cache after applyCoupon:', e)
                } finally {
                    dispatch(fareFamilyApi.util.invalidateTags(['FareFamily']))
                }
            }
        }),
        removeDiscount: builder.mutation<
            RemoveDiscountResponse,
            { itineraryId: string; payload: RemoveDiscountPayload }
        >({
            query: ({ itineraryId, payload }) => ({
                url: `/itin/${itineraryId}/remove-discount`,
                method: 'PUT',
                body: payload
            }),
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (data?.data?.coupon === 'success') {
                        dispatch(
                            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                                if (draft?.itineraryData) {
                                    draft.itineraryData.couponDetails = []
                                }
                            })
                        )
                        dispatch(fareFamilyApi.util.invalidateTags(['FareFamily']))
                    }
                } catch (e) {
                    console.log('Failed to update coupon cache after removeDiscount:', e)
                }
            }
        }),
        getCouponOffers: builder.query<ApiCouponOffer[], { isEgyptDomain: boolean }>({
            query: ({ isEgyptDomain }) => {
                const endpoint = isEgyptDomain
                    ? 'https://ui.cltp.co/coupons/eg-flyin/index.json'
                    : 'https://ui.cltp.co/coupons/flyin/index.json'
                return {
                    url: endpoint,
                    method: 'GET'
                }
            }
        })
    })
})

export const { useApplyCouponMutation, useRemoveDiscountMutation, useGetCouponOffersQuery } = couponApi
